import { useEffect, useRef, useState } from "react";
import {
  ChevronRight,
  File,
  FileImage,
  FileText,
  Folder,
  FolderPlus,
  Star,
  Trash2,
  Upload,
} from "lucide-react";
import { fetchJsonCached, invalidateApiCache } from "../../../app/api/cache";
import { PromptModal } from "../../../components/ui/confirm-modal";

interface FileRecord {
  id: string;
  parentFolderId?: string | null;
  filename: string;
  size: number;
  mimeType?: string;
  type?: "file" | "folder";
  pinned: boolean;
  createdAt: string;
  updatedAt?: string;
}

function fmt(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "Yesterday";
  return `${days}d ago`;
}

function FileIcon({ item }: { item: FileRecord }) {
  if (item.type === "folder") {
    return <Folder size={18} className="text-secondary shrink-0" />;
  }
  const mime = item.mimeType ?? "";
  if (mime.startsWith("image/")) {
    return <FileImage size={18} className="text-secondary shrink-0" />;
  }
  if (mime.startsWith("text/") || mime === "application/pdf") {
    return <FileText size={18} className="text-secondary shrink-0" />;
  }
  return <File size={18} className="text-secondary shrink-0" />;
}

export function FilesPanel() {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [pinnedItems, setPinnedItems] = useState<FileRecord[]>([]);
  const [folderStack, setFolderStack] = useState<FileRecord[]>([]);
  const [status, setStatus] = useState("");
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);
  const folderUploadRef = useRef<HTMLInputElement>(null);

  const currentFolder = folderStack[folderStack.length - 1] ?? null;

  const loadFiles = async (folderId = currentFolder?.id) => {
    try {
      const query = folderId ? `?parentFolderId=${encodeURIComponent(folderId)}` : "";
      setFiles(await fetchJsonCached<FileRecord[]>(`/api/files${query}`));
    } catch {
      // offline
    }
  };

  const loadPinnedItems = async () => {
    try {
      setPinnedItems(await fetchJsonCached<FileRecord[]>("/api/files?pinned=true"));
    } catch {
      // offline
    }
  };

  useEffect(() => {
    loadFiles();
    loadPinnedItems();
  }, [currentFolder?.id]);

  useEffect(() => {
    folderUploadRef.current?.setAttribute("webkitdirectory", "");
    folderUploadRef.current?.setAttribute("directory", "");
  }, []);

  const uploadFile = async (file: File, parentFolderId: string | null) => {
    const fd = new FormData();
    fd.append("file", file);
    if (parentFolderId) fd.append("parentFolderId", parentFolderId);
    return fetch("/api/files", {
      method: "POST",
      credentials: "include",
      body: fd,
    });
  };

  const createFolder = async (name: string, parentFolderId: string | null) => {
    const res = await fetch("/api/files/folders", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, parentFolderId }),
    });
    if (!res.ok) throw new Error("Folder create failed");
    return res.json() as Promise<FileRecord>;
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus(`Uploading ${file.name}…`);
    const res = await uploadFile(file, currentFolder?.id ?? null);
    e.target.value = "";
    if (res.ok) {
      invalidateApiCache("/api/files");
      await loadFiles();
      await loadPinnedItems();
      setStatus("");
    } else {
      setStatus("Upload failed");
    }
  };

  const handleFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    if (selectedFiles.length === 0) return;

    const folderIds = new Map<string, string>();
    setStatus(`Uploading ${selectedFiles.length} files…`);

    try {
      for (const file of selectedFiles) {
        const relativePath = file.webkitRelativePath || file.name;
        const parts = relativePath.split("/").filter(Boolean);
        const folders = parts.slice(0, -1);
        let parentId = currentFolder?.id ?? null;
        let pathKey = "";

        for (const folderName of folders) {
          pathKey = pathKey ? `${pathKey}/${folderName}` : folderName;
          if (!folderIds.has(pathKey)) {
            const folder = await createFolder(folderName, parentId);
            folderIds.set(pathKey, folder.id);
          }
          parentId = folderIds.get(pathKey) ?? parentId;
        }

        const res = await uploadFile(file, parentId);
        if (!res.ok) throw new Error("File upload failed");
      }

      invalidateApiCache("/api/files");
      await loadFiles();
      await loadPinnedItems();
      setStatus("");
    } catch {
      setStatus("Folder upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const handleCreateFolder = async (name: string) => {
    setIsCreateFolderOpen(false);
    setStatus("Creating folder…");
    try {
      await createFolder(name, currentFolder?.id ?? null);
      invalidateApiCache("/api/files");
      await loadFiles();
      setStatus("");
    } catch {
      setStatus("Could not create folder");
    }
  };

  const handleDelete = async (item: FileRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    const label = item.type === "folder" ? "folder and its contents" : "file";
    if (!window.confirm(`Delete this ${label}?`)) return;
    await fetch(`/api/files/${item.id}`, { method: "DELETE", credentials: "include" });
    invalidateApiCache("/api/files");
    setFiles((prev) => prev.filter((f) => f.id !== item.id));
    setPinnedItems((prev) => prev.filter((f) => f.id !== item.id));
  };

  const handleTogglePinned = async (item: FileRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    const nextPinned = !item.pinned;
    setFiles((prev) =>
      prev.map((f) => f.id === item.id ? { ...f, pinned: nextPinned } : f),
    );
    setPinnedItems((prev) =>
      nextPinned
        ? [{ ...item, pinned: true }, ...prev.filter((f) => f.id !== item.id)]
        : prev.filter((f) => f.id !== item.id),
    );

    const res = await fetch(`/api/files/${item.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pinned: nextPinned }),
    });
    invalidateApiCache("/api/files");
    if (!res.ok) {
      await loadFiles();
      await loadPinnedItems();
    }
  };

  const handleDownload = async (f: FileRecord) => {
    if (f.type === "folder") {
      setFolderStack((prev) => [...prev, f]);
      return;
    }

    const res = await fetch(`/api/files/${f.id}/download`, { credentials: "include" });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openBreadcrumb = (index: number) => {
    setFolderStack(index < 0 ? [] : folderStack.slice(0, index + 1));
  };

  return (
    <div className="p-md sm:p-margin-page w-full max-w-[1200px] mx-auto overflow-auto overflow-x-hidden h-full">
      {isCreateFolderOpen && (
        <PromptModal
          title="Create folder"
          placeholder="Folder name"
          onSubmit={handleCreateFolder}
          onCancel={() => setIsCreateFolderOpen(false)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-md mb-xl">
        <div>
          <h1 className="font-h1 text-primary">Archive</h1>
          <p className="font-body-md text-secondary mt-1">
            Manage and organize your files.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-sm mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => setIsCreateFolderOpen(true)}
            className="bg-surface-container-low text-primary border border-outline-variant font-label-sm px-4 py-2 rounded flex items-center gap-2 hover:bg-surface-container-high transition-colors"
          >
            <FolderPlus size={18} />
            New folder
          </button>
          <label className="bg-surface-container-low text-primary border border-outline-variant font-label-sm px-4 py-2 rounded flex items-center gap-2 hover:bg-surface-container-high transition-colors cursor-pointer">
            <Folder size={18} />
            Upload folder
            <input
              ref={folderUploadRef}
              type="file"
              className="hidden"
              multiple
              onChange={handleFolderUpload}
            />
          </label>
          <label className="bg-primary text-on-primary font-label-sm px-4 py-2 rounded flex items-center gap-2 hover:opacity-90 transition-opacity shadow-sm cursor-pointer">
            <Upload size={18} />
            Upload
            <input ref={uploadRef} type="file" className="hidden" onChange={handleUpload} />
          </label>
        </div>
      </div>

      {status && (
        <p className="font-body-md text-secondary mb-md">{status}</p>
      )}

      <section className="mb-xl">
        <h2 className="font-label-sm text-secondary uppercase tracking-widest mb-md select-none">
          Pinned
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-md">
          {pinnedItems.length === 0 ? (
            <div className="border border-dashed border-outline-variant rounded p-lg text-secondary font-body-md">
              Pin files or folders with the star action.
            </div>
          ) : (
            pinnedItems.slice(0, 4).map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => handleDownload(item)}
                className="bg-surface-container-lowest border border-outline-variant rounded p-lg flex items-center gap-md hover:bg-surface-variant hover:border-outline cursor-pointer transition-colors text-left"
              >
                <div className="w-10 h-10 rounded bg-surface-container-low flex items-center justify-center text-primary">
                  <FileIcon item={item} />
                </div>
                <div className="min-w-0">
                  <span className="block font-label-sm text-primary truncate">{item.filename}</span>
                  <span className="block font-mono-sm text-outline">
                    {item.type === "folder" ? "Folder" : fmt(item.size)}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </section>

      <section className="mb-xl">
        <div className="flex flex-wrap items-center gap-2 mb-md font-label-sm text-secondary uppercase tracking-widest select-none">
          <button
            type="button"
            onClick={() => openBreadcrumb(-1)}
            className="hover:text-primary transition-colors"
          >
            Root
          </button>
          {folderStack.map((folder, index) => (
            <span key={folder.id} className="flex items-center gap-2">
              <ChevronRight size={14} className="text-outline-variant" />
              <button
                type="button"
                onClick={() => openBreadcrumb(index)}
                className={`transition-colors ${
                  index === folderStack.length - 1
                    ? "text-primary"
                    : "hover:text-primary"
                }`}
              >
                {folder.filename}
              </button>
            </span>
          ))}
        </div>

        <div className="bg-surface-container-lowest border border-outline-variant rounded shadow-sm overflow-hidden">
          <table className="w-full table-fixed text-left border-collapse">
            <thead>
              <tr className="border-b border-outline-variant bg-surface-container-low">
                <th className="px-md sm:px-lg py-3 font-label-sm text-secondary font-medium uppercase tracking-wider">Name</th>
                <th className="px-md sm:px-lg py-3 font-label-sm text-secondary font-medium uppercase tracking-wider hidden sm:table-cell">Size</th>
                <th className="px-lg py-3 font-label-sm text-secondary font-medium uppercase tracking-wider hidden sm:table-cell">Modified</th>
                <th className="px-md sm:px-lg py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant text-primary font-body-md">
              {files.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-lg py-8 text-center text-secondary italic">
                    No files here yet. Upload a file or folder to get started.
                  </td>
                </tr>
              ) : (
                files.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => handleDownload(item)}
                    className="hover:bg-surface-variant transition-colors group cursor-pointer"
                  >
                    <td className="px-md sm:px-lg py-4 flex items-center gap-3 min-w-0">
                      <FileIcon item={item} />
                      <span className="truncate">{item.filename}</span>
                    </td>
                    <td className="px-md sm:px-lg py-4 font-mono-sm text-outline group-hover:text-secondary hidden sm:table-cell">
                      {item.type === "folder" ? "Folder" : fmt(item.size)}
                    </td>
                    <td className="px-lg py-4 font-mono-sm text-outline group-hover:text-secondary hidden sm:table-cell">
                      {relTime(item.updatedAt ?? item.createdAt)}
                    </td>
                    <td className="px-md sm:px-lg py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={(e) => handleTogglePinned(item, e)}
                          className={`transition-colors ${
                            item.pinned
                              ? "text-primary"
                              : "text-outline hover:text-primary opacity-0 group-hover:opacity-100"
                          }`}
                          title={item.pinned ? "Unpin" : "Pin"}
                        >
                          <Star size={16} className={item.pinned ? "fill-primary" : ""} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDelete(item, e)}
                          className="text-outline hover:text-error transition-colors opacity-0 group-hover:opacity-100"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
